import { Progress } from '@/components/ui/Progress';
import type { TaskStatus } from '@/lib/api';

interface ProgressBarProps {
  status: TaskStatus;
  label: string;
  icon: React.ReactNode;
}

export function ProgressBar({ status, label, icon }: ProgressBarProps) {
  const { progress, completed, total, status: taskStatus, inProgress } = status;

  const getStatusColor = () => {
    switch (taskStatus) {
      case 'running': return 'bg-primary-500';
      case 'completed': return 'bg-green-500';
      case 'failed': return 'bg-red-500';
      default: return 'bg-gray-600';
    }
  };

  const getStatusText = () => {
    switch (taskStatus) {
      case 'running': return inProgress ? `处理中: ${inProgress}` : '运行中';
      case 'completed': return '已完成';
      case 'failed': return '失败';
      default: return '待启动';
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-gray-200">{label}</h3>
        </div>
        <span className={`text-sm px-2 py-1 rounded ${
          taskStatus === 'running' ? 'bg-primary-500/20 text-primary-400' :
          taskStatus === 'completed' ? 'bg-green-500/20 text-green-400' :
          taskStatus === 'failed' ? 'bg-red-500/20 text-red-400' :
          'bg-gray-700/50 text-gray-400'
        }`}>
          {getStatusText()}
        </span>
      </div>

      <Progress value={progress} className="h-3">
        <div className={`h-full ${getStatusColor()} transition-all duration-500`} style={{ width: `${progress}%` }} />
      </Progress>

      <div className="flex justify-between text-sm text-gray-400">
        <span>{completed} / {total} 完成</span>
        <span>{progress}%</span>
      </div>
    </div>
  );
}
